import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from 'lucide-react';
type Preview = {
  title: string;
  description: string;
  imageUrl: string;
};
const previews: Preview[] = [{
  title: 'Weekly Master Sequences',
  description: 'The full week mapped pose by pose, in order, so you can teach the whole class at a glance.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/aAvcvR1KcDNJxKC8TdkdFD/Captura-de-pantalla-2026-07-28-a-las-8.22.46_p._m..webp'
}, {
  title: 'Pose-by-Pose Breakdown',
  description: 'Every posture with its focus and a teacher cue you can say out loud, exactly as written.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/dfPjcAPh9ryVcbhcXh4nJo/Captura-de-pantalla-2026-07-28-a-las-8.21.28_p._m..webp'
}, {
  title: 'Visual Alignment Cues',
  description: 'Variation A and B side by side, with alignment points and the cue that unlocks the pose.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9GssxgVXua54ibYJAGukpf/Captura-de-pantalla-2026-07-28-a-las-8.17.59_p._m..webp'
}, {
  title: 'Core-Centric Flow Maps',
  description: 'Annotated anatomy on the pose itself, plus the progressions that build real stability.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/d7gLtP5TpK1zTYfwFGSHgL/Captura-de-pantalla-2026-07-28-a-las-8.20.46_p._m..webp'
}, {
  title: 'Teaching Concept Guides',
  description: 'The “why” behind each block, so you can explain your method with professional confidence.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/hqkJfpkF8XftLSsJm2dN9c/Captura-de-pantalla-2026-07-28-a-las-8.18.48_p._m..webp'
}];
export function InsideMaterialCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activePreview = previews[activeIndex];
  const goToPrevious = () => {
    setActiveIndex(currentIndex => currentIndex === 0 ? previews.length - 1 : currentIndex - 1);
  };
  const goToNext = () => {
    setActiveIndex(currentIndex => currentIndex === previews.length - 1 ? 0 : currentIndex + 1);
  };
  return <section className="bg-[#fff7fb] px-5 py-20" data-id="element-133" data-mp="1vytln1hwhfyv">
      <div className="mx-auto max-w-6xl" data-id="element-134" data-mp="1vxidkphf9m1y">
        <div className="mx-auto max-w-3xl text-center" data-id="element-135" data-mp="20q38juc2e572">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.2em] text-emerald-700" data-id="element-136" data-mp="20qlcze8vrpq2">
            <EyeIcon className="h-4 w-4" data-id="element-137" data-mp="25qqxmagqgbyn" /> See what&apos;s inside
          </p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-none text-[#241322] sm:text-5xl" data-id="element-138" data-mp="182yy39u7cynw">
            Made to use in the <span className="text-emerald-700" data-id="element-139" data-mp="1hzbre6lu5ai4">real world</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm font-medium leading-relaxed text-slate-600 sm:text-base" data-id="element-140" data-mp="1i1espmsfuq25">
            Open a volume and find the plan, cue, or visual map you need — without starting from a blank page.
          </p>
        </div>

        <div className="mt-11 grid overflow-hidden border border-[#ead9e5] bg-white lg:grid-cols-[minmax(0,1fr)_310px]" data-id="element-141" data-mp="2ycse2e5z6zxo">
          <div className="relative flex min-h-[510px] items-center justify-center bg-[#f7edf4] p-5 sm:p-8" data-id="element-142" data-mp="mpoy5a4wej9k">
            <img key={activePreview.imageUrl} src={activePreview.imageUrl} alt={`Preview of the ${activePreview.title} included in Yoga Ready`} width={728} height={1280} className="max-h-[620px] w-auto max-w-full border border-[#ead9e5] object-contain shadow-2xl" data-id="element-143" data-mp="hrx7ejfzp36y" />
            <button type="button" onClick={goToPrevious} aria-label="Show previous material preview" className="absolute left-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/70 bg-[#241622]/90 text-white shadow-lg transition-colors hover:bg-[#a72a67] sm:left-6 sm:h-14 sm:w-14" data-id="element-144" data-mp="3v5s5fes3m5xd">
              <ChevronLeftIcon className="h-6 w-6" data-id="element-145" data-mp="1jg7p0mem286p" />
            </button>
            <button type="button" onClick={goToNext} aria-label="Show next material preview" className="absolute right-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/70 bg-[#241622]/90 text-white shadow-lg transition-colors hover:bg-[#a72a67] sm:right-6 sm:h-14 sm:w-14" data-id="element-146" data-mp="h332dmrpg8zy">
              <ChevronRightIcon className="h-6 w-6" data-id="element-147" data-mp="c5t7hrljr54q" />
            </button>
          </div>

          <aside className="flex flex-col bg-[#241622] p-7 text-left sm:p-9" data-id="element-148" data-mp="3tidgy9z4vfc5">
            <p className="text-xs font-bold uppercase tracking-[.18em] text-emerald-300" data-id="element-149" data-mp="27dbpbbkr7ow">
              Preview {String(activeIndex + 1).padStart(2, '0')} / {String(previews.length).padStart(2, '0')}
            </p>
            <h3 className="mt-4 font-display text-4xl font-bold uppercase leading-none text-white" data-id="element-150" data-mp="3pgs7kylt5j6p">
              {activePreview.title}
            </h3>
            <p className="mt-5 text-sm font-medium leading-relaxed text-slate-300" data-id="element-151" data-mp="3fkym2pq42fcs">
              {activePreview.description}
            </p>
            <p className="mt-auto border-t border-emerald-300/20 pt-6 text-xs font-bold uppercase tracking-[.14em] text-emerald-200" data-id="element-152" data-mp="2vz6qvu1rr8b3">
              A closer look at your ready-to-use tools
            </p>
            <div className="mt-5 flex gap-2" aria-label="Choose a material preview" data-id="element-153" data-mp="35v03tejeh3te">
              {previews.map((preview, index) => <button key={preview.title} type="button" onClick={() => setActiveIndex(index)} aria-label={`Show ${preview.title}`} aria-pressed={index === activeIndex} className={`h-2.5 w-2.5 rounded-full transition-colors ${index === activeIndex ? 'bg-emerald-300' : 'bg-white/30 hover:bg-white/60'}`} data-id="element-154" data-mp="3ex9atufgmf21" data-mpi={index} />)}
            </div>
          </aside>
        </div>
      </div>
    </section>;
}